/**
 * The MUI theme. App-wide design decisions live here and nowhere else.
 * See ./README.md for what does and does not belong.
 *
 * The theme is a **function of the palette mode**, not a constant: light and
 * dark are the same design with two palettes, so every decision below is made
 * once and both modes inherit it. `ColorModeProvider` calls this and memoises
 * the result.
 */
import { alpha, createTheme, type CSSObject, type Theme } from '@mui/material/styles';
import type { PaletteMode } from '@mui/material';
import pageBackground from '../assets/login-bg.jpg';

/**
 * The Fortes brand colours, verbatim.
 *
 * These are the *source* values and the only place they appear as hex. Nothing
 * else in the app may write a hex code — components read `theme.palette.*`, so
 * a brand change happens here and nowhere else.
 */
export const brandColors = {
  purple: '#1A0549',
  pink: '#FF1449',
  green: '#6CED48',
  cyan: '#00CFFF',
} as const;

/**
 * The height of the top bar, in pixels.
 *
 * It lives here rather than in `layout/` because it drives `mixins.toolbar`,
 * which is what sizes both the real `Toolbar` and the empty one that clears the
 * fixed `AppBar`. The sidebar reads the same constant for its logo block, so
 * the two line up across the drawer edge.
 */
export const TOP_BAR_HEIGHT = 71;

/**
 * The shell's rhythm: the gap, in spacing units, between the main panel and
 * the screen edge, and between one sidebar group and the next.
 */
export const SHELL_GAP = 1.5;

/** The corner radius of every glass surface in the shell, in pixels. */
export const GLASS_RADIUS = 20;

/**
 * How opaque each kind of glass is. `panel` is the main body and the two
 * clusters in the top bar; `group` is a sidebar section, thinner so the
 * sidebar reads as one level below the panel it is attached to.
 */
const GLASS_OPACITY = {
  panel: 0.9,
  group: 0.65,
} as const;

export type GlassKind = keyof typeof GLASS_OPACITY;

const GLASS_BLUR = 'blur(20px) saturate(160%)';

/**
 * The shell's "aero" material: the paper colour, partly transparent, over a
 * blurred copy of what is behind it. Shape and margins stay at the call site.
 *
 * `backdrop-filter` makes the element the containing block for any
 * `position: fixed` descendant. MUI portals its popovers and dialogs to
 * `body`, so they are unaffected; a fixed element written inline is not.
 */
export function glass(theme: Theme, kind: GlassKind): CSSObject {
  return {
    backgroundColor: alpha(theme.palette.background.paper, GLASS_OPACITY[kind]),
    backdropFilter: GLASS_BLUR,
    WebkitBackdropFilter: GLASS_BLUR,
  };
}

/**
 * Builds the theme for one palette mode.
 *
 * Light runs the brand navy as both the primary colour and the text colour, on
 * white. Dark cannot: navy on near-black is unreadable, so the primary lightens
 * to a lilac that keeps the hue and passes contrast, and takes the navy back as
 * its `contrastText`.
 */
export function createAppTheme(mode: PaletteMode): Theme {
  return createTheme({
    palette: {
      mode,

      ...(mode === 'light'
        ? {
            background: {
              default: '#FFFFFF',
              paper: '#FFFFFF',
            },
            text: {
              primary: brandColors.purple,
              secondary: '#514766',
            },
            primary: {
              main: brandColors.purple,
              contrastText: '#FFFFFF',
            },
          }
        : {
            background: {
              default: '#111016',
              paper: '#1B1922',
            },
            text: {
              primary: '#FFFFFF',
              secondary: '#C7C3D0',
            },
            primary: {
              main: '#B9A4E8',
              contrastText: '#1A0549',
            },
          }),

      secondary: {
        main: brandColors.pink,
        contrastText: '#FFFFFF',
      },

      error: {
        main: '#D32F2F',
      },

      warning: {
        main: '#ED6C02',
      },

      info: {
        main: brandColors.cyan,
        contrastText: '#1A0549',
      },

      success: {
        main: brandColors.green,
        contrastText: '#1A0549',
      },

      divider: mode === 'light' ? '#E6E1EE' : '#393541',
    },

    typography: {
      fontFamily: '"Inter", "Roboto", Arial, sans-serif',
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 700 },
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },

    shape: {
      borderRadius: 10,
    },

    mixins: {
      // Replaces MUI's responsive default outright: one height at every
      // breakpoint, the same as the sidebar's logo block.
      toolbar: { minHeight: TOP_BAR_HEIGHT },
    },

    components: {
      // The photograph behind every screen, the sign-in screen and the shell
      // alike. A fixed layer rather than a `body` background, so it stays put
      // while a page scrolls on every browser — `background-attachment: fixed`
      // does not on iOS. Dark mode lays its own background colour over the
      // photograph: the sky is pale, and the white wordmark sits on it.
      MuiCssBaseline: {
        styleOverrides: (theme: Theme) => {
          const scrim = alpha(theme.palette.background.default, 0.6);
          const layers = [`url(${pageBackground})`];
          if (theme.palette.mode === 'dark') layers.unshift(`linear-gradient(${scrim}, ${scrim})`);

          return {
            // The colour moves from `body` to `html`. `index.html` gives `html`
            // a background for the first frame, and while `html` has one,
            // `body` paints its own — over the layer below, at z-index -1.
            html: { backgroundColor: theme.palette.background.default },
            body: { backgroundColor: 'transparent' },
            'body::before': {
              content: '""',
              position: 'fixed',
              inset: 0,
              zIndex: -1,
              pointerEvents: 'none',
              backgroundImage: layers.join(', '),
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            },
          };
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },

      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
      },
    },
  });
}
